
import { Component, Vue } from 'nuxt-property-decorator'
import { ApolloError } from 'apollo-boost'
import VueI18n, { TranslateResult } from 'vue-i18n'
import { Emit, Ref } from 'vue-property-decorator'
import { ApolloCurrentQueryResult } from 'apollo-client'
import SkeletonLoaderCollection from '@mg/web-components/Atoms/SkeletonLoaderCollection'
import OrderLoader from '@mg/web-components/Order/OrderLoader'
import BasicTitle from '@mg/web-components/Base/Typography/BasicTitle'
import OrderDetailBasic from '@mg/web-components/Order/OrderDetailBasic'
import LoginBox from '@mg/web-components/Atoms/LoginBox'
import { FormInput, RoundedButton } from '@mg/micro/vue2'
import BasicButton from '@mg/web-components/Atoms/Buttons/BasicButton'
import { Order, OrderDeliveryEvent } from '@mg/web-components/Order/types'
import { FeaturesConfig } from '@mg/web-components/types'
import ApolloFetchPolicies from '~/utils/apolloFetchPolicies'
import { GQLOrder } from '~/graphql/__generated__/interfaces'
import { GET_ORDER_DETAIL_PUBLIC } from '~/graphql/gql/order'
import OrderActionsManager from '~/components/order/OrderActionsManager.vue'
import PageTitle from '~/components/PageTitle.vue'
import { GQL_LOCAL_ACCOUNT } from '~/graphql/gqlLocal/localAccount'
import { GQLLocalAccount } from '~/graphql/__generated__/interfacesLocal'
import { createUrl } from '~/utils/routes'
import ROUTES from '~/config/routes'
import {
  LoginBoxTranslated,
  OrderDetailBasicTranslated,
  OrderTrackingTranslated,
} from '~/components/order/translations'
import { OrderDeliveryEventWithOrder } from '~/components/order/events'
import OrderFactory from '~/utils/orderHelpers/orderFactory'
import { OrderHelper } from '~/utils/orderHelpers/orderHelper'
import { trackWebKcPageView } from '~/utils/tracking/gtm/pageViews'
import { logApolloError } from '~/utils/apolloErrors'
import { getValidOrderOr } from '~/utils/order'
import DeliveryTimelineFactory from '~/utils/orderHelpers/timelineFactory'
import BannerWrapper from '~/components/banners/BannerWrapper.vue'

type OrderResult = ApolloCurrentQueryResult<{
  order: GQLOrder
}>

@Component<DeliveryDetailPage>({
  components: {
    PageTitle,
    BasicTitle,
    BannerWrapper,
    FormInput,
    RoundedButton,
    BasicButton,
    LoginBox,
    SkeletonLoaderCollection,
    OrderLoader,
    OrderDetailBasic,
  },
  apollo: {
    account: {
      fetchPolicy: ApolloFetchPolicies.NETWORK_ONLY,
      query: GQL_LOCAL_ACCOUNT,
    },
    order: {
      fetchPolicy: ApolloFetchPolicies.NETWORK_ONLY,
      prefetch: false,
      query() {
        return GET_ORDER_DETAIL_PUBLIC
      },
      skip() {
        return !!this.$apollo.queries.account.loading || !this.orderId
      },
      variables() {
        return {
          id: this.orderId,
        }
      },
      context() {
        const headers = {} as any
        if (this.hash) {
          headers['x-parceltracking-token'] = this.hash
        }
        return { headers }
      },
      error(e: ApolloError) {
        logApolloError(e, 'DeliveryPage - order query error', this.$logger)
        this.errorMessage = this.translations.fetchError
      },
      result(data: OrderResult) {
        if (this.checkNotFound(data)) {
          this.errorMessage = this.translations.invalidOrderId
          return
        }
        trackWebKcPageView(
          this,
          'common.txt_client_center_my_orders',
          'order.delivery_detail_gtm'
        )

        if (this.order && this.hasSingleDelivery) {
          this.$router.push({
            path: createUrl(
              ROUTES.orderTrackingDetail,
              this.$shopConfig.shopId,
              {
                params: {
                  orderId: this.orderId,
                  deliveryId: this.order.deliveries[0].deliveryId,
                },
                query: { h: this.hash as string },
              }
            ),
          })
        }
      },
    },
  },
  head() {
    return {
      title: this.translations.pageOrderTrackingTitle as string,
    }
  },
})
export default class DeliveryDetailPage extends Vue {
  @Ref() readonly orderActionsManager!: OrderActionsManager

  account: GQLLocalAccount
  order: GQLOrder
  errorMessage: TranslateResult = ''
  inputOrderId: String = ''

  orderHelper: OrderHelper = new OrderHelper(this.$shopConfig)

  timelineFactory: DeliveryTimelineFactory = new DeliveryTimelineFactory()

  orderFactory: OrderFactory = new OrderFactory(
    this.orderHelper,
    this.timelineFactory,
    this.$shopConfig,
    this.$t.bind(this)
  )

  get orderId(): string {
    return getValidOrderOr(this.$route?.params?.orderId, '')
  }

  get hasSingleDelivery() {
    return this.order.deliveries.length === 1
  }

  get hash(): string | undefined {
    return this.$route?.query?.h as string
  }

  get isLoggedIn(): boolean {
    return !!this.$shopConfig.user.id || !!this.account?.isLogged
  }

  get loginUrl(): string {
    return createUrl(ROUTES.userLogin, this.$shopConfig.shopId)
  }

  get showOrderDetail(): boolean {
    return !!this.orderId && !this.errorMessage
  }

  get featuresConfig(): FeaturesConfig {
    return this.$shopConfig.featuresConfig
  }

  translate(
    key: VueI18n.Path,
    values?: VueI18n.Values
  ): VueI18n.TranslateResult {
    return this.$t(key, values)
  }

  mounted() {
    this.$Progress.finish()
  }

  get shopId(): string {
    return this.$shopConfig.shopId
  }

  async viewOrder(): Promise<void> {
    const inputOrderId = String(this.inputOrderId).trim()
    if (!inputOrderId) {
      return
    }

    // validate order id
    const numericOrderId = Number(inputOrderId)
    if (isNaN(numericOrderId)) {
      this.errorMessage = this.translations.invalidOrderId
      return
    }

    this.errorMessage = ''

    if (this.orderId === inputOrderId) {
      await this.$apollo.queries.order.refresh()
    } else {
      await this.$router.push({
        path: createUrl(ROUTES.orderTracking, this.$shopConfig.shopId, {
          params: {
            orderId: inputOrderId,
          },
          query: { h: this.hash as string },
        }),
      })
    }
  }

  @Emit()
  openDeliveryDetail(
    event: OrderDeliveryEvent
  ): OrderDeliveryEventWithOrder | void {
    const { deliveryId, orderId } = event

    this.$router.push({
      path: createUrl(ROUTES.orderTrackingDetail, this.$shopConfig.shopId, {
        params: { orderId, deliveryId },
        query: { h: this.hash as string },
      }),
    })
  }

  get extendedOrder(): Order | null {
    const order = this.order
    if (!order) {
      return null
    }
    return this.orderFactory.createOrder(order)
  }

  get isLoading(): boolean {
    return !this.order
  }

  get loadingSingleDelivery(): boolean {
    return !!this.order && this.hasSingleDelivery
  }

  checkNotFound(data: OrderResult): boolean {
    return !!data.errors?.find(
      (error) => error.extensions?.code === 'NOT_FOUND'
    )
  }

  get loginBoxTranslations(): LoginBoxTranslated {
    return {
      show_detailed_info: this.$t('orders.kc_show_detailed_info'),
      login_reversed: this.$t('orders.kc_login_reversed'),
    }
  }

  get translations(): OrderTrackingTranslated {
    return {
      enterOrderIdToView: this.$t('orders.enter_order_id_to_view'),
      findOrderIdInEmailSubject: this.$t(
        'orders.find_order_id_in_email_subject'
      ),
      orderId: this.$t('orders.order_id'),
      fetchError: this.$t('orders.fetch_order_error'),
      invalidOrderId: this.$t('orders.invalid_order_id'),
      viewOrder: this.$t('orders.view_order'),
      pageOrderTrackingTitle: this.$t('common.order_tracking'),
      pageOrderDetailTitle: this.$t('orders.pur_purchases'),
    }
  }

  get orderDetailBasicTranslations(): OrderDetailBasicTranslated {
    return {
      order_nr: this.$t('orders.order_nr'),
      cart_preview_delivery_mall: this.$t('orders.cart_preview_delivery_mall'),
      delivery_detail: this.$t('orders.delivery_detail'),
      'txt_product-list_count_1': this.$t('common.txt_product-list_count_1'),
      'txt_product-list_count_2': this.$t('common.txt_product-list_count_2'),
      'txt_product-list_count_2-4': this.$t(
        'common.txt_product-list_count_2-4'
      ),
      'txt_product-list_count_5': this.$t('common.txt_product-list_count_5'),
      order_created: this.$t('orders.order_created'),
      orders_delivery_by: this.$t('orders.orders_delivery_by'),
      pt_delivery_to_pup: this.$t('orders.pt_delivery_to_pup'),
      pt_delivered_to_pup: this.$t('orders.pt_delivered_to_pup'),
      pt_delivered: this.$t('orders.pt_delivered'),
      pickup_by: this.$t('orders.pickup_by'),
      pt_label_address: this.$t('orders.pt_label_address'),
      pt_picked_up: this.$t('orders.pt_picked_up'),
      pt_at_the_address: this.$t('orders.pt_at_the_address'),
      shipment_origin: this.$t('orders.shipment_origin'),
      mall_name: this.$t('common.mall_name'),
      delayed_info_tomorrow: this.$t('orders.delayed_info_tomorrow'),
      smart_delivery_waiting_for_payment: this.$t(
        'orders.smart_delivery_waiting_for_payment'
      ),
      smart_delivery_valid_to: this.$t('orders.smart_delivery_valid_to'),
    }
  }
}
